import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { AreaDto, AreaListDto } from '@lib/dto/area.dto'
import type { CreateAreaInput, UpdateAreaInput } from '@lib/input/area.input'
import { clientService } from '@/services/ClientService'
import { proposalKeys, areaKeys } from '@/hooks/queryKeys'

/**
 * The areas drawn on one site, oldest first.
 *
 * A draft proposal resolves membership from these rings, so a save also
 * refreshes any open proposal document.
 */
export function useAreas(siteId: string): UseQueryResult<AreaListDto> {
  return useQuery({
    queryKey: areaKeys.list(siteId),
    queryFn: () => clientService.listAreas(siteId),
    enabled: siteId !== '',
    retry: false,
  })
}

function useInvalidateAreas(siteId: string): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: areaKeys.list(siteId) })
    void queryClient.invalidateQueries({ queryKey: proposalKeys.documents() })
  }
}

export function useAddArea(
  siteId: string,
): UseMutationResult<AreaDto, Error, CreateAreaInput> {
  const invalidate = useInvalidateAreas(siteId)
  return useMutation({
    mutationFn: (input: CreateAreaInput) =>
      clientService.addArea(siteId, input),
    onSuccess: invalidate,
  })
}

export function useUpdateArea(
  siteId: string,
): UseMutationResult<AreaDto, Error, { id: string; input: UpdateAreaInput }> {
  const invalidate = useInvalidateAreas(siteId)
  return useMutation({
    mutationFn: ({ id, input }) => clientService.updateArea(id, input),
    onSuccess: invalidate,
  })
}

export function useRemoveArea(
  siteId: string,
): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateAreas(siteId)
  return useMutation({
    mutationFn: (id: string) => clientService.removeArea(id),
    onSuccess: invalidate,
  })
}
